import {
  createHotelReadCache as createReadCache,
  loadHotelDetails as loadDetails,
  loadHotelSearch as loadSearch,
  rewriteHotelDetails,
  type HotelReadCache as ReadCache,
} from '@voll-intelligence/intelligence-ui-kit';

import {
  hotelFreeCancellationUntil,
  type HotelDetailsResult,
  type HotelPlannerPort,
  type ChatUiHotelRoom,
  type HotelPlannerSelection,
  type HotelSearchPreviewResult,
} from '@voll-intelligence/genui-contract';

/* O cache de leitura mora no kit (`HotelReadCache`), com as leituras injetadas.
   Aqui ele ganha os tipos do wire e a porta do planner, e fica a correção da
   tarifa pelo quote, que lê a regra de cancelamento do wire. */

export { disposeHotelReadCache, primeHotelDetails } from '@voll-intelligence/intelligence-ui-kit';

type CachedRead<T> = Promise<T> | T;

export type HotelReadCache = ReadCache<HotelSearchPreviewResult, HotelDetailsResult>;

export function createHotelReadCache(): HotelReadCache {
  return createReadCache();
}

function hotelSearchKey(selection: HotelPlannerSelection): string {
  return JSON.stringify(selection);
}

export function loadHotelSearch(
  cache: HotelReadCache,
  port: HotelPlannerPort,
  selection: HotelPlannerSelection,
  sessionId?: string,
): CachedRead<HotelSearchPreviewResult> {
  return loadSearch(cache, hotelSearchKey(selection), (signal) =>
    port.searchHotels(selection, signal, sessionId),
  );
}

export function loadHotelDetails(
  cache: HotelReadCache,
  port: Pick<HotelPlannerPort, 'getHotelDetails'>,
  offerId: string,
): CachedRead<HotelDetailsResult> {
  return loadDetails(cache, offerId, (signal) => port.getHotelDetails(offerId, signal));
}

interface HotelConfirmedCancellation {
  readonly refundable?: boolean;
  /** ISO; ausente = não há prazo sem multa pela frente. */
  readonly freeCancellationUntil?: string;
}

/** A tarifa passa a contar a condição que o quote confirmou. As janelas da
 *  busca ficam só enquanto ainda levam ao mesmo prazo: são elas que dão as
 *  multas à seção "Política de cancelamento", mas o card as relê antes do
 *  campo pronto (`freeCancellationFromRoom`) e voltaria a anunciar o prazo
 *  velho. */
function withConfirmedCancellation(
  room: ChatUiHotelRoom,
  confirmed: HotelConfirmedCancellation,
): ChatUiHotelRoom {
  const next: ChatUiHotelRoom = {
    ...room,
    ...(confirmed.refundable === undefined ? {} : { refundable: confirmed.refundable }),
  };
  const windowsAgree =
    next.refundable !== false &&
    hotelFreeCancellationUntil(room.cancellationPolicies ?? [], next.refundable) ===
      confirmed.freeCancellationUntil;
  if (!windowsAgree) Reflect.deleteProperty(next, 'cancellationPolicies');
  if (confirmed.freeCancellationUntil) next.freeCancellationUntil = confirmed.freeCancellationUntil;
  else Reflect.deleteProperty(next, 'freeCancellationUntil');
  return next;
}

/** O que uma leitura mais fresca da tarifa (o quote) corrige nela. */
export type HotelDetailsRatePatch = Partial<Pick<ChatUiHotelRoom, 'total' | 'policyStatus'>> & {
  /** A condição de cancelamento que o quote confirmou (C3). */
  readonly cancellation?: HotelConfirmedCancellation;
};

/**
 * Reconcilia UMA tarifa de um detalhe com uma leitura mais fresca dela (#3631)
 * — o quote relê a tarifa no fornecedor depois da lista, e a lista não pode
 * seguir com o preço e o selo da leitura anterior enquanto o checkout já usa
 * os do quote. `roomIndex` é a posição 1-based do quarto no `rooms` ORIGINAL
 * do detalhe, a mesma identidade que a captura de preço consome.
 *
 * `total` é o de UM quarto pela estadia (a semântica de `rooms[].total`); quem
 * tem o total de N quartos converte antes. Trocar o total derruba o
 * `totalPerNight` da leitura anterior: nenhum preço dela sobrevive ao lado do
 * novo. Devolve um detalhe NOVO (o recebido não muda), ou `undefined` quando o
 * quarto não existe nele.
 */
function patchHotelDetailsRoom(
  details: HotelDetailsResult,
  roomIndex: number,
  patch: HotelDetailsRatePatch,
): HotelDetailsResult | undefined {
  const room = details.rooms[roomIndex - 1];
  if (!room) return undefined;
  const { cancellation, ...fields } = patch;
  const next: ChatUiHotelRoom = cancellation
    ? withConfirmedCancellation({ ...room, ...fields }, cancellation)
    : { ...room, ...fields };
  if (patch.total) Reflect.deleteProperty(next, 'totalPerNight');
  const rooms = details.rooms.map((candidate, index) =>
    index === roomIndex - 1 ? next : candidate,
  );
  return { ...details, rooms };
}

/** `patchHotelDetailsRoom` sobre o detalhe deste cache, avisando quem o lê.
 *  Detalhe ainda pendente, ausente ou quarto fora da lista: no-op — não há
 *  leitura na tela para corrigir. */
export function patchHotelDetailsRate(
  cache: HotelReadCache,
  offerId: string,
  roomIndex: number,
  patch: HotelDetailsRatePatch,
): void {
  rewriteHotelDetails(cache, offerId, (details) =>
    patchHotelDetailsRoom(details, roomIndex, patch),
  );
}
